import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import React, {useEffect, useState} from 'react';

import {LoaderProxy} from '../../containers/AppLoader';
import {FullScreenPanelContainer} from '../Panel/FullScreenPanel/FullScreenPanel';
import {FullScreenPanelNotFound} from '../Panel/FullScreenPanel/FullScreenPanelNotFound';
import {useFullScreenPanelLookup} from '../Panel/FullScreenPanel/useFullScreenPanelLookup';
import {useAutoSyncPanelBankContext} from '../PanelBank/contextProviders/AutoSyncPanelBankContext';
import {PanelBank} from '../PanelBank/PanelBank';
import {RunDetails} from '../RunDetails/RunDetails';
import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useWorkspaceSettingsContext} from '../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {WorkspaceModePrompt} from '../WorkspaceModePrompt/WorkspaceModePrompt';
import {SingleRunNotFound, useSingleRunModeContext} from './SingleRunMode';
import {MultiRunWorkspaceAllProps} from './types';
import {WorkspaceWithRunSelector} from './WorkspaceWithRunSelector';

export type MultiRunWorkspaceContentContainerProps = {
  children?: React.ReactNode;
  entityName: string;
  isProjectReadOnly: boolean;
  isSingleMode: boolean;
  isTableExpanded: boolean;
  onSelectRunName: MultiRunWorkspaceAllProps['onSelectRunName'];
  onSetTableExpanded: MultiRunWorkspaceAllProps['onSetTableExpanded'];
  panelBankWidth: number;
  project: MultiRunWorkspaceAllProps['project'];
  projectName: string;
  recommendedGrouping: MultiRunWorkspaceAllProps['recommendedGrouping'];
  runSetRef: MultiRunWorkspaceAllProps['runSetRef'];
  sectionPart: MultiRunWorkspaceAllProps['sectionPart'];
  selectedRunName: string | undefined;
  setPanelBankWidth: (width: number) => void;
  showArtifactCounts: boolean;
  showLogCounts: boolean;
  title: string | React.ReactElement;
  workspaceID: string;
};

export const MultiRunWorkspaceContentContainer = React.memo(
  ({
    children,
    entityName,
    isProjectReadOnly,
    isSingleMode,
    isTableExpanded,
    onSelectRunName,
    onSetTableExpanded,
    panelBankWidth,
    project,
    projectName,
    recommendedGrouping,
    runSetRef,
    sectionPart,
    selectedRunName,
    setPanelBankWidth,
    showArtifactCounts,
    showLogCounts,
    title,
    workspaceID,
  }: MultiRunWorkspaceContentContainerProps) => {
    const {isSingleRun} = useSingleRunModeContext();
    const [shouldShowRunSelector, setShouldShowRunSelector] = useState(
      !isSingleRun
    );

    const {
      workspaceLayout: {shouldAutoGeneratePanels},
    } = useWorkspaceSettingsContext();

    if (shouldAutoGeneratePanels === 'pending') {
      return <WorkspaceModePrompt />;
    }

    return (
      <WorkspaceWithRunSelector
        isProjectReadOnly={isProjectReadOnly}
        isSingleMode={isSingleMode}
        isTableExpanded={isTableExpanded}
        onSelectRunName={onSelectRunName}
        onSetTableExpanded={onSetTableExpanded}
        panelBankWidth={panelBankWidth}
        project={project}
        recommendedGrouping={recommendedGrouping}
        runSetRef={runSetRef}
        sectionPart={sectionPart}
        selectedRunName={selectedRunName}
        setPanelBankWidth={setPanelBankWidth}
        shouldShowRunSelector={shouldShowRunSelector}
        showArtifactCounts={showArtifactCounts}
        showLogCounts={showLogCounts}
        title={title}>
        <WorkspaceContent
          entityName={entityName}
          isProjectReadOnly={isProjectReadOnly}
          isTableExpanded={isTableExpanded}
          projectName={projectName}
          workspaceID={workspaceID}
          setShouldShowRunSelector={setShouldShowRunSelector}>
          {children}
        </WorkspaceContent>
      </WorkspaceWithRunSelector>
    );
  }
);

export const WorkspaceContent = ({
  children,
  entityName,
  isProjectReadOnly,
  isTableExpanded,
  projectName,
  workspaceID,
  setShouldShowRunSelector,
}: {
  children?: React.ReactNode;
  entityName: string;
  isProjectReadOnly: boolean;
  isTableExpanded: boolean;
  projectName: string;
  workspaceID: string;
  setShouldShowRunSelector: (shouldShowRunSelector: boolean) => void;
}) => {
  const {responsiveSize} = useWindowSize();
  const {isLoading: isAutoSyncingPanelBank} = useAutoSyncPanelBankContext();
  const {
    isSingleRun,
    isLoading: isLoadingSingleRun,
    run,
  } = useSingleRunModeContext();
  const isLoading = isLoadingSingleRun || isAutoSyncingPanelBank;

  const {isFullScreenPanelPage, panelRefAndPanelBankSectionConfigRef} =
    useFullScreenPanelLookup();
  const isFullScreenPanelNotFound =
    !isLoading &&
    isFullScreenPanelPage &&
    !panelRefAndPanelBankSectionConfigRef;
  const isSingleRunNotFound = isSingleRun && !isLoading && !run;
  const isMobileWorkspaceTab = !isTableExpanded && responsiveSize !== 'large';

  useEffect(() => {
    if (isFullScreenPanelNotFound || isMobileWorkspaceTab || isSingleRun) {
      setShouldShowRunSelector(false);
    } else if (!isLoading) {
      setShouldShowRunSelector(true);
    }
  }, [
    isFullScreenPanelNotFound,
    isLoading,
    isMobileWorkspaceTab,
    isSingleRun,
    setShouldShowRunSelector,
  ]);

  if (isLoading) {
    return <LoaderProxy name="multi-run-workspace-content" />;
  }

  if (isFullScreenPanelPage && !panelRefAndPanelBankSectionConfigRef) {
    return <FullScreenPanelNotFound />;
  }

  if (isSingleRunNotFound) {
    return <SingleRunNotFound />;
  }

  if (isFullScreenPanelPage && panelRefAndPanelBankSectionConfigRef) {
    // Render full screen panel within the run selector context to preserve state
    // when users navigate back from full screen view (avoiding full page reload)
    return (
      <SectionSettingsContextProvider
        sectionRef={
          panelRefAndPanelBankSectionConfigRef.panelBankSectionConfigRef
        }>
        <FullScreenPanelContainer
          isProjectReadOnly={isProjectReadOnly}
          panelRef={panelRefAndPanelBankSectionConfigRef.panelRef}
          panelBankSectionConfigRef={
            panelRefAndPanelBankSectionConfigRef.panelBankSectionConfigRef
          }
          className="h-full w-full overflow-auto"
        />
      </SectionSettingsContextProvider>
    );
  }

  if (children) {
    return <div style={{flexGrow: 1, overflow: 'auto'}}>{children}</div>;
  }

  if (isTableExpanded) {
    return null;
  }

  const panelBank = (
    <PanelBank
      entityName={entityName}
      projectName={projectName}
      isProjectReadOnly={isProjectReadOnly}
      workspaceID={workspaceID}
    />
  );

  if (isSingleRun) {
    return <RunDetails chartsContent={panelBank} />;
  }

  return panelBank;
};
